import { ANALYTICS_CONSENT_KEY, analyticsConsentNeeded } from '@geosport/shared';
import { create } from 'zustand';
import { readString, writeString } from './storage';

export type Consent = 'granted' | 'denied';

const stored = (): Consent | null => {
  const v = readString(ANALYTICS_CONSENT_KEY);
  return v === 'granted' || v === 'denied' ? v : null;
};

function timeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** Global Privacy Control counts as "no" unless the visitor opts in themselves. */
const gpc = (): boolean =>
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

interface ConsentState {
  /** The visitor's explicit answer, or null when they haven't been asked or answered. */
  choice: Consent | null;
  choose: (choice: Consent) => void;
}

export const useConsent = create<ConsentState>((set) => ({
  choice: stored(),
  choose: (choice) => {
    writeString(ANALYTICS_CONSENT_KEY, choice);
    set({ choice });
  },
}));

/**
 * DECISION: analytics runs after an explicit yes, or with no answer where opt-in isn't required
 * (outside the EEA/UK/CH by time zone, see analyticsConsentNeeded) and no GPC signal. Same rule
 * as the content pages' inline loader (apps/api/src/site/layout.ts).
 */
export function analyticsAllowed(choice: Consent | null): boolean {
  return choice === 'granted' || (choice === null && !analyticsConsentNeeded(timeZone()) && !gpc());
}

/** Ask only where opt-in is required, before any answer, and never over a GPC "no". */
export function shouldAskConsent(choice: Consent | null): boolean {
  return choice === null && analyticsConsentNeeded(timeZone()) && !gpc();
}
